/**
 * store-plugin — the `/store` Genome Store surface as a studio-sibling plugin.
 *
 * #116d (the Store MVP capstone): the visible Store surface that BROWSES the
 * served `store/index.json` (the #116b loader) and INSTALLS artifacts through the
 * REAL #116c engine. Mirrors `agentPlugin` / `ideShellPlugin`: one `component`
 * editor (`catalog`) routed at `/store`, composed into the ONE studio genome
 * (gate 4 — `/store` resolves to the registered `catalog` editor).
 *
 * The editor is `React.lazy` so the whole Store surface + `@ubiclouds/v5-store`
 * stay in a split chunk OUT of the app entry (gate 8 — entry < 150 KB gz). The
 * route id is derived from the path (`/store` → `store`); `routesTo:
 * 'component/catalog'` auto-expands to this plugin's `:component/catalog` entity.
 *
 * NO DOCK PANEL (#1099 — retired at `IDE_LAYOUT_VERSION` 28). Between #499 and v27
 * this module ALSO registered a LEFT region dock panel, which made the marketplace
 * the default-selected LEFT tab of every fresh `/ide` — the first thing a workspace
 * showed was a shop. A marketplace is a settings-shaped concern, not a workspace
 * region: it is browsed occasionally and never while editing, so it now lives at the
 * `/store` route and inside the Settings modal's `plugins` category, and the LEFT
 * border is Files/Search/Git — the panels a user actually works from.
 *
 * The deletion is only HALF the change, which is why {@link STORE_PANEL_ID} still
 * exists: dropping a registration clears the FRESH default, but a persisted layout
 * loads verbatim (#909 S4), so an existing account would render `Unknown panel:
 * v5-studio-store-panel` forever. The `REMOVED_TAB_IDS` row in
 * `ide/layout-migration-rows.ts` is the withdrawal path, and the #1074 delivery gate's
 * R2 rule is what makes it non-optional.
 *
 * @module @ubiclouds/v5-studio/store/store-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The Store-surface plugin id. */
export const STORE_PLUGIN_ID = 'v5-studio-store';

// The retired dock-panel id is re-exported (it lives in a dependency-free module so the
// React-free `layout-migration-rows` ledger can name it), keeping every existing
// importer of `store-plugin.js` unchanged.
export { STORE_PANEL_ID } from './store-panel-id.js';

/** The split-chunk Store surface (the `/store` route editor + the Settings pane). */
const LazyStoreSurface = lazy(() => import('./StoreSurface.js'));

/**
 * The Store plugin — the `/store` route → the lazy `catalog` editor. Composed as
 * a SIBLING in `studioPlugins`; it adds no new `EntityKind` (the surface lowers
 * onto `component`/`route`), so gate 4 stays green.
 */
export const storePlugin: ComposableGenomePlugin = definePlugin(STORE_PLUGIN_ID)
  .name('Store')
  .trustTier('core')
  .editor('catalog', LazyStoreSurface)
  .route('/store', { routesTo: 'component/catalog', title: 'Store' })
  .build();
